import { reactive, ref, App, toRaw } from 'vue';
import { useApplicationStore } from '@/store/application.store';
import isEqual from 'lodash/isEqual';
import { extractSimpleAddress } from '@/utils/addressFormatter';

// Define interfaces
export interface ChangeItem {
  field: string;
  originalValue: any;
  newValue: any;
}

// Create a singleton store for unsaved changes
const unsavedChanges = ref(false);
const detectedChanges = reactive<ChangeItem[]>([]);
const initialLoadComplete = ref(false);
const initialChangesProcessed = ref(false);

// Set a timer to mark initial load as complete
setTimeout(() => {
  initialLoadComplete.value = true;
}, 2000);

// Local helper: build a comparable address string including apartment/subpremise when present
const toComparableAddressString = (val: any): string | null => {
  try {
    if (val === null || val === undefined) return null;
    // Use existing extractor as baseline
    let base = extractSimpleAddress(val as any) || '';

    // If we can, enrich with subpremise/apt from address_components
    const geocode = (val as any)?.geocode ?? (val as any)?.address_components ? (val as any) : undefined;
    const components: any[] | undefined = geocode?.address_components;
    if (Array.isArray(components)) {
      const sub = components.find((c: any) => Array.isArray(c.types) && c.types.includes('subpremise'));
      const apt = sub?.long_name || sub?.short_name;
      if (apt) {
        base = `${base}`.trim();
        // Append in a stable, non-visual way for comparison
        base = `${base} | apt:${String(apt)}`;
      }
      // If base was empty, synthesize from components
      if (!base || base === ' | apt:' + String(apt)) {
        const get = (t: string, s: 'short_name' | 'long_name' = 'long_name') => {
          const c = components.find((x: any) => Array.isArray(x.types) && x.types.includes(t));
          return c ? (c[s] || c.long_name || c.short_name || '') : '';
        };
        const line1 = `${get('street_number', 'short_name')} ${get('route')}`.trim();
        const cityState = [get('locality'), get('administrative_area_level_1', 'short_name')].filter(Boolean).join(', ');
        const cityStatePostal = [cityState, get('postal_code', 'short_name')].filter(Boolean).join(' ');
        const country = get('country', 'short_name');
        const parts = [line1, cityStatePostal, country].filter(Boolean);
        base = parts.join(', ');
        if (apt) base = `${base} | apt:${String(apt)}`;
      }
    }

    if (!base) return null;
    const normalize = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ').replace(/,\s*/g, ', ');
    return normalize(base);
  } catch {
    return null;
  }
};

// Improved comparison function to handle special cases
const areValuesSame = (a: any, b: any): boolean => {
  // Handle null/undefined cases
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a === undefined || b === undefined) return false;

  // Handle dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle empty strings
  if ((a === "" && b === null) || (a === null && b === "")) return true;
  if ((a === "" && b === undefined) || (a === undefined && b === "")) return true;

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < Number.EPSILON;
  }

  // Handle address-like values by comparing normalized strings (including apt/subpremise)
  const aAddr = toComparableAddressString(a);
  const bAddr = toComparableAddressString(b);
  if (aAddr !== null || bAddr !== null) {
    return (aAddr || '') === (bAddr || '');
  }

  // Handle plain objects and arrays using lodash isEqual
  return isEqual(a, b);
};

// Helper function to add a specific change
const addChange = (field: string, originalValue: any, newValue: any) => {
  // Skip adding if the values are actually the same using our robust comparison
  if (areValuesSame(originalValue, newValue)) {
    return;
  }

  // Look for an existing change with this field name
  const existingIndex = detectedChanges.findIndex((change) => change.field === field);
  if (existingIndex >= 0) {
    // Update the existing change
    detectedChanges[existingIndex] = { field, originalValue, newValue };
  } else {
    // Add a new change
    detectedChanges.push({ field, originalValue, newValue });
  }
  
  unsavedChanges.value = detectedChanges.length > 0;
  
  // Update application state - only if initial load is complete
  if (initialLoadComplete.value) {
    useApplicationStore().setState({ key: 'showDynamicBanner', value: detectedChanges.length > 0 });
  }
};

// Helper function to clear changes
const clearChanges = () => {
  detectedChanges.length = 0;
  unsavedChanges.value = false;
  useApplicationStore().setState({ key: 'showDynamicBanner', value: false });
};

// Main function to set unsaved changes
const setUnsavedChangesFn = (value: boolean, changes?: Array<{ field: string, originalValue: any, newValue: any }>) => {
  // Skip updates during initial load or if we're already processed initial changes
  if (!initialChangesProcessed.value) {
    initialChangesProcessed.value = true;
    
    // If we're setting to false, just clear and return
    if (!value) {
      clearChanges();
      return;
    }
  }
  
  if (value) {
    if (changes && changes.length > 0) {
      // Don't clear existing changes, just update/add the new ones
      // This ensures we don't lose tracked changes when new ones come in
      
      // Process each change
      for (const change of changes) {
        const plainChange = {
          field: change.field,
          originalValue: toRaw(change.originalValue),
          newValue: toRaw(change.newValue)
        };
        
        // Check if this is a reverted change (new value matches original value)
        if (areValuesSame(plainChange.originalValue, plainChange.newValue)) {
          // Check if we already have this field
          const existingIndex = detectedChanges.findIndex(c => c.field === plainChange.field);
          
          if (existingIndex >= 0) {
            detectedChanges.splice(existingIndex, 1);
          }
        } else {
          // This is a real change (values are different)
          // Check if we already have this field
          const existingIndex = detectedChanges.findIndex(c => c.field === plainChange.field);
          
          if (existingIndex >= 0) {
            detectedChanges[existingIndex] = plainChange;
          } else {
            detectedChanges.push(plainChange);
          }
        }
      }
      
      // Force update of unsavedChanges
      unsavedChanges.value = detectedChanges.length > 0;
      
      // Set the banner - only if initial load is complete
      if (initialLoadComplete.value) {
        useApplicationStore().setState({ key: 'showDynamicBanner', value: detectedChanges.length > 0 });
      }
    }
  } else {
    clearChanges();
  }
};

// The public API
const unsavedChangesApi = {
  // Methods
  setUnsavedChanges: setUnsavedChangesFn,
  addUnsavedChange: addChange,
  clearUnsavedChanges: clearChanges,
  
  // Reactive state
  hasUnsavedChanges: unsavedChanges,
  changes: detectedChanges,
  initialLoadComplete,
  initialChangesProcessed
};

// Create the Vue plugin
export default {
  install: (app: App) => {
    // Make the API available globally
    app.config.globalProperties.$unsavedChanges = unsavedChangesApi;
    
    // Provide the API to all components
    app.provide('unsavedChanges', unsavedChangesApi);
    
    // Also provide individual functions for backward compatibility
    app.provide('UnsavedChangesWrapper:setUnsavedChanges', setUnsavedChangesFn);
    app.provide('UnsavedChangesWrapper:addUnsavedChange', addChange);
    app.provide('UnsavedChangesWrapper:clearUnsavedChanges', clearChanges);
  }
};

// Export a composable for use in components
export function useUnsavedChanges() {
  return unsavedChangesApi;
} 